import React from "react"
import { styled, keyframes } from "@mui/material/styles"
import { css } from '@emotion/react';
import { Checkbox, Fade, LinearProgress } from "@mui/material"
import { observer, inject } from "mobx-react"
import { getVolume, stopAllAudioExceptSlug } from "../utils/userUtils"
import AudioPlayer from "./AudioPlayer"

const shadowSlide = keyframes`
  0% { width: 0; }
  100% { width: 100%; }
`

const CardPlayShadow = styled('div')(({ duration }) => css`
  height: 100%;
  width: 0;
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0.08;
  z-index: 50;
  background-color: #000;
  border-radius: 5px 0 0 0;
  animation: ${shadowSlide} ${duration}s linear infinite;
`);

const SampleWaveformContainer = styled('div')`
  cursor: pointer;
  position: relative;
  height: 100%;
`

const CardImageContainer = styled('div')`
  position: relative;
  height: 100%;
`

const CardImage = styled('img')(({ assignedColor }) => css`
  height: 100%;
  width: 100%;
  border-radius: 5px;
  background-color: ${assignedColor} !important;
`)

const WaveformTitle = styled('div')`
  position: absolute;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.7) 30%,
    rgba(255, 255, 255, 1) 100%
  );
  padding: 10px 20px;
  font-size: 20px;
  font-weight: 300;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  color: black;
  box-sizing: border-box;
`

const SFCheckbox = styled(Checkbox)`
  z-index: 2;
  position: absolute;
  right: 0;
  top: 0;

  input {
    // materializecss mucks this up
    pointer-events: inherit !important;
  }
`;

const SFLinearProgress = styled(LinearProgress)`
  border-radius: 5px 5px 0 0;
  position: absolute;
  width: 100%;
`

const SampleTimestamp = styled('div')`
  position: absolute;
  top: 5px;
  left: 5px;
  color: ${(props) => (props.isPublished ? "#4db6ac" : "#2196F3")};
  font-size: 13px;
`

// In order to optionally check if certain stores are injected
// https://github.com/mobxjs/mobx-react/issues/232
@inject((props, stores, context) => props)
@observer
export default class SampleWaveform extends React.Component {
  constructor(props) {
    super(props)
    const volume = getVolume()

    this.state = {
      isPlaying: false,
      isLoading: false,
      isLoaded: false,
      shouldLoop: true,
      volumeValue: volume,
    }

    this.audioPlayer = React.createRef()
    this.debouncedCreatePlay = _.debounce(props.sample.createPlay, 1000, {leading: true, trailing: false})
  }

  static defaultProps = {
    displayTitle: true,
    shouldRenderTimestampsIfMod: true,
  }

  componentDidMount() {
    if (typeof(window) !== "undefined") {
      window.addEventListener("volumeChanged", this.setVolume)
      window.addEventListener("stopAllAudioExcept", this.stopAudioExcept)
      window.addEventListener("triggerSamplesPlayback", this.triggerPlayback)
    }
  }

  componentWillUnmount() {
    if (typeof(window) !== "undefined") {
      window.removeEventListener("volumeChanged", this.setVolume)
      window.removeEventListener("stopAllAudioExcept", this.stopAudioExcept)
      window.removeEventListener("triggerSamplesPlayback", this.triggerPlayback)
    }
    // Ensure sound is stopped and state is reset when component unmounts
    this.stopSound()
    // Cancel any pending debounced calls
    this.debouncedCreatePlay.cancel()
  }

  stopAudioExcept = (event) => {
    const { sample, sampleStore } = this.props
    if (event.detail.slugPlaying !== sample.slug && sampleStore?.canPlayMultipleSamplesAtOnce === false) {
      this.stopSound()
    }
  }

  setVolume = () => {
    const volume = getVolume()
    this.setState({ volumeValue: volume })
  }

  createPlay = () => {
    this.debouncedCreatePlay()
  }

  playSound = (e) => {
    const { sample, sampleStore } = this.props
    if (sample.enabledForPlay === false) {
      console.log("Sample play disabled")
      return
    }

    if (!sampleStore?.canPlayMultipleSamplesAtOnce) {
      stopAllAudioExceptSlug(sample.slug)
    }

    this.setState({ shouldLoop: true })
    this.audioPlayer.current.play()
    this.createPlay()
    sample.setToPlay()
  }

  playSoundOnce = () => {
    const { sample } = this.props
    if (sample.enabledForPlay === false) {
      return
    }
    this.setState({
      shouldLoop: false,
    })
    this.createPlay()
    sample.setToPlay()
    this.audioPlayer.current.play()
    stopAllAudioExceptSlug(sample.slug)
  }

  stopSound = (e) => {
    const { sample } = this.props
    this.setState({
      isPlaying: false,
    })
    sample.setToStop()
    this.audioPlayer.current.stop()
  }

  onIsLoading = () => {
    this.setState({ isLoading: true })
  }

  onIsLoaded = () => {
    this.setState({
      isLoading: false,
      isLoaded: true
    })
  }

  onPlay = () => {
    this.setState({ isPlaying: true })
  }

  triggerPlayback = (event) => {
    const { shouldPlay } = event.detail
    if (shouldPlay) {
      this.playSoundOnce()
    } else {
      this.stopSound()
    }
  }

  onFinishedPlaying = () => {
    const { sample } = this.props
    sample.setToStop()
    this.setState({ isPlaying: false })
  }

  handleCheckboxClick = (e) => {
    e.stopPropagation()
  }

  renderTimestamps = () => {
    const { sample } = this.props
    const timeStampToDisplay = sample.published_at
      ? sample.published_at.toDateString()
      : sample.created_at.toDateString()
    const iconClassname = sample.published_at ? "fas fa-check" : "fas fa-upload"

    return (
      <SampleTimestamp isPublished={Boolean(sample.published_at)}>
        <i className={iconClassname} aria-hidden="true"></i>
        {" "}{timeStampToDisplay}
      </SampleTimestamp>
    )
  }

  handleCheckboxSelect = (event) => {
    const { sample, sampleStore } = this.props
    if (!sampleStore) return

    const didSelect = event.target.checked
    if (sampleStore.showSequencer) {
      return
    }

    if (didSelect) {
      sampleStore.selectSample(sample)
    } else {
      sampleStore.deselectSample(sample)
    }
  }

  handleCheckboxSequencerSelect = (event) => {
    event.stopPropagation()
    const { sample, sampleStore } = this.props;
    const didSelect = event.target.checked;

    if (didSelect) {
      sampleStore.selectSampleForSequencer(sample);
    } else {
      sampleStore.deselectSampleForSequencer(sample);
    }
  }

  renderSampleSelect = () => {
    const { sample } = this.props
    return (
      <>
        <SFCheckbox
          color="primary"
          checked={sample.isSelected}
          onChange={this.handleCheckboxSelect}
          onClick={this.handleCheckboxClick}
          inputProps={{ 'aria-label': `Checkbox to select sample` }}
        />
      </>
    )
  }

  renderSampleSequencerSelect() {
    const { sample, sampleStore } = this.props;
    const index = sampleStore.selectedSamplesForSequencer.indexOf(sample);
    const label = index >= 0 ? (index + 1).toString() : '';

    const CheckedIcon = (
      <div
        style={{
          width: 17,
          height: 17,
          backgroundColor: sample.assigned_color || '#1A73E8',
          opacity: 0.6,
          borderRadius: 2,
          border: '2px solid rgba(0, 0, 0, 1.0)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          margin: "3px 3px 0 0"
        }}
      >
        <span
          style={{
            color: '#FFFFFF',
            fontSize: '12px',
          }}
        >
          {label}
        </span>
      </div>
    );


    return (
      <SFCheckbox
        checked={sample.isSelectedForSequencer}
        onChange={this.handleCheckboxSequencerSelect}
        onClick={this.handleCheckboxClick}
        checkedIcon={CheckedIcon}
      />
    );
  }

  render() {
    const {
      displayTitle,
      sample,
      showSampleSelect,
      handleWaveformClick,
      userStore,
      sampleStore,
      shouldRenderTimestampsIfMod
    } = this.props

    const {
      isPlaying,
      isLoading,
      shouldLoop,
      volumeValue,
    } = this.state

    return (
      <SampleWaveformContainer
        className="sample-card-link"
        onMouseOver={this.playSound}
        onMouseLeave={this.stopSound}
        onClick={handleWaveformClick}
      >
        <AudioPlayer
          url={sample.sample_mp3_url || sample.audioMp3Url}
          ref={this.audioPlayer}
          volume={volumeValue}
          shouldLoop={shouldLoop}
          onPlay={this.onPlay}
          onFinishedPlaying={this.onFinishedPlaying}
          onIsLoading={this.onIsLoading}
          onIsLoaded={this.onIsLoaded}
        />
        <CardImageContainer>
          {userStore?.currentUser?.canModerate && shouldRenderTimestampsIfMod && this.renderTimestamps()}
          {showSampleSelect && sampleStore && !sampleStore.showSequencer && this.renderSampleSelect()}
          {showSampleSelect && sampleStore.showSequencer && this.renderSampleSequencerSelect()}

          <Fade in={isLoading}>
            <SFLinearProgress color="primary" role="progressbar" />
          </Fade>
          <CardImage
            src={sample.sample_waveform_url || sample.audioWaveformUrl}
            assignedColor={sample.assigned_color}
            role="img"
            alt={`Sample waveform image`}
          />
          {isPlaying && <CardPlayShadow duration={sample.duration} />}
          {displayTitle && <WaveformTitle role="heading">{sample.displayName}</WaveformTitle>}
        </CardImageContainer>
      </SampleWaveformContainer>
    )
  }
}